
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function About() {
  const [showSchedulePopup, setShowSchedulePopup] = useState(false);

  const values = [
    {
      title: 'Strategic Partnership',
      description: 'We believe in building long-term partnerships with founders, providing ongoing support beyond initial investment.'
    },
    {
      title: 'Financial Discipline',
      description: 'Our banking background ensures rigorous financial analysis and sustainable growth strategies for portfolio companies.'
    },
    {
      title: 'Cross-Border Expertise',
      description: 'Deep understanding of both European innovation and Latin American market dynamics creates unique value.'
    },
    {
      title: 'Operational Excellence',
      description: 'Hands-on approach to helping founders structure operations, optimize capital, and navigate regulatory challenges.'
    }
  ];

  const timeline = [
    {
      year: 'Q1 2026',
      title: 'Open Applications',
      description: 'We begin accepting pitches from Europe\'s most ambitious founders looking to scale into LATAM. Our submission portal and \"Pitch Us\" screening process will open to early-stage and seed companies ready for expansion capital and operational partnership.'
    },
    {
      year: 'Q3 2026',
      title: 'Screening & Diagnosis',
      description: 'Applications close, and our team begins a rigorous diagnostic process — assessing product strength, financial readiness, founder-market fit, and LATAM scalability potential. Only the most promising ventures move forward into the accelerator phase.'
    },
    {
      year: 'Q4 2026',
      title: 'Pilot & Launch',
      description: 'EULA VC launches its first accelerator cohort, combining strategic capital with hands-on operational mentorship. The pilot phase activates our proprietary LATAM Market Diagnostic Engine and begins our hands-on growth partnership with selected founders.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl md:text-4xl font-bold text-white mb-4 md:mb-6 text-left md:text-center" style={{ fontFamily: '"Playfair Display", serif' }}>
              About EULA VC
            </h1>
            <div className="space-y-4 md:space-y-6 text-left md:text-center">
              <p className="text-base md:text-xl text-white leading-relaxed">
                We are a Luxembourg-based venture capital firm uniquely positioned to bridge the gap between 
                European innovation and Latin American market opportunities.
              </p>
              <p className="text-sm md:text-lg text-white leading-relaxed">
                Founded by senior banking executives with deep LATAM experience, we combine traditional 
                financial discipline with venture capital innovation to help European startups successfully 
                expand across Latin America.
              </p>
              <p className="text-sm md:text-lg text-white leading-relaxed">
                Our mission is to empower visionary European founders with the capital, strategic expertise, 
                and market intelligence needed to unlock the vast potential of Latin American markets.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="text-left md:text-center lg:text-left">
              <h2 className="text-xl md:text-3xl font-bold text-slate-900 mb-4 md:mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
                Our Mission
              </h2>
              <p className="text-sm md:text-lg text-slate-600 mb-4 md:mb-6">
                To empower European founders with the capital, expertise, and strategic guidance needed 
                to successfully enter and scale across Latin American markets.
              </p>
              <p className="text-sm md:text-lg text-slate-600">
                We believe that the combination of European innovation and Latin American market 
                dynamics creates unprecedented opportunities for growth and impact.
              </p>
            </div>
            <div className="text-left md:text-center lg:text-left">
              <h2 className="text-xl md:text-3xl font-bold text-slate-900 mb-4 md:mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
                Our Vision
              </h2>
              <p className="text-sm md:text-lg text-slate-600 mb-4 md:mb-6">
                To become the leading bridge between European startup ecosystems and Latin American 
                markets, creating a new generation of global companies.
              </p>
              <p className="text-sm md:text-lg text-slate-600">
                We envision a future where geographic boundaries don't limit innovation, and where 
                the best European startups can seamlessly scale across LATAM.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-xl md:text-3xl font-bold text-slate-900 mb-3 md:mb-4 text-left md:text-center">Our Values</h2>
            <p className="text-base md:text-xl text-slate-600 max-w-3xl mx-auto text-left md:text-center">
              The principles that guide our investment decisions and partnership approach.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-6 md:p-8 rounded-lg shadow-sm">
                <h3 className="text-base md:text-xl font-semibold text-slate-900 mb-3 md:mb-4 text-left">
                  {value.title}
                </h3>
                <p className="text-xs md:text-base text-slate-600 text-left">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose EULA VC */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-xl md:text-3xl font-bold text-slate-900 mb-8 md:mb-12 text-left md:text-center" style={{ fontFamily: '"Playfair Display", serif' }}>
            Why Choose EULA VC
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              <h2 className="text-xl md:text-3xl font-bold text-slate-900 mb-4 md:mb-6">Our Story</h2>
              <p className="text-sm md:text-lg text-slate-600 mb-4 md:mb-6">
                EULA VC was born from the recognition that European startups face significant challenges 
                when expanding into Latin American markets, despite the enormous opportunities these 
                markets present.
              </p>
              <p className="text-sm md:text-lg text-slate-600 mb-4 md:mb-6">
                Our founders, with combined 25+ years of senior banking experience across LATAM markets 
                at institutions like Santander and Bank of America, witnessed firsthand the gap between 
                European innovation and Latin American market entry.
              </p>
              <p className="text-sm md:text-lg text-slate-600 mb-6 md:mb-8">
                We established EULA VC to fill this gap, providing not just capital but the strategic 
                expertise, operational guidance, and market insights that European founders need to 
                succeed in LATAM.
              </p>
              <Link to="/founders">
                <Button variant="primary">Meet Our Team</Button>
              </Link>
            </div>
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/f2b557645282e69e9aadbcce9c1a2166.jpg"
                alt="Luxembourg financial district"
                className="rounded-lg shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Investment Approach */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-xl md:text-3xl font-bold text-slate-900 mb-8 md:mb-12 text-left md:text-center" style={{ fontFamily: '"Playfair Display", serif' }}>
            Our Investment Approach
          </h2>
          <div className="max-w-4xl mx-auto text-center md:text-center">
            <div className="text-sm md:text-lg text-slate-600 leading-relaxed space-y-4 md:space-y-6 text-left md:text-center">
              <p>
                We believe that seed capital is only the beginning. Funding alone is not enough to build enduring companies — it must be coupled with strategic guidance, operational rigor, and real-world experience. Every dollar we invest is paired with the blood, sweat, and tears of seasoned professionals who are deeply committed to helping your company grow and thrive.
              </p>
              <p>
                Our approach goes beyond financing. If your venture passes our rigorous screening process, you gain access to 30+ years of multi-generational banking experience, uniquely focused on Latin America. From Brazil to Mexico, Argentina to Chile and Colombia, we help founders navigate complex markets, scale operations, optimize finances, and unlock regional growth opportunities.
              </p>
              <p className="text-base md:text-xl font-semibold text-slate-900">
                With us, investment is a partnership — a hands-on journey where capital meets expertise, and ambition meets execution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-xl md:text-3xl font-bold text-slate-900 mb-8 md:mb-12 text-left md:text-center" style={{ fontFamily: '"Playfair Display", serif' }}>
            Our Journey
          </h2>
          <div className="space-y-8">
            {timeline.map((item, index) => (
              <div key={index} className="flex flex-col md:flex-row items-start md:items-center gap-6">
                <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 bg-slate-900 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-xs md:text-sm">{item.year}</span>
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-base md:text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-xs md:text-base text-slate-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-xl md:text-3xl font-bold text-white mb-4 md:mb-6 text-left md:text-center" style={{ fontFamily: '"Playfair Display", serif' }}>
            Ready to Transform Your Startup?
          </h2>
          <p className="text-base md:text-xl text-slate-300 mb-6 md:mb-8 max-w-3xl mx-auto text-left md:text-center">
            Join our mission to bridge Europe and Latin America. Whether you're a founder ready to expand 
            or an investor interested in your approach, we'd love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/pitch">
              <Button variant="primary" size="lg" className="bg-slate-100 text-slate-900 hover:bg-white hover:text-slate-900">
                Apply for Investment
              </Button>
            </Link>
            <Link to="/pitch">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-slate-900">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Schedule Meeting Popup */}
      {showSchedulePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-line text-slate-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Schedule a Meeting
              </h3>
              <p className="text-slate-600 mb-6">
                To schedule a call with our team, please first submit your pitch materials through our "Pitch Us" page. 
                This helps us prepare for a more productive conversation about your LATAM expansion plans.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/pitch" className="flex-1">
                  <Button variant="primary" className="w-full">
                    Submit Pitch Materials
                  </Button>
                </Link>
                <button
                  onClick={() => setShowSchedulePopup(false)}
                  className="px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
